import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import Loader from 'react-loader-spinner';
import {
  FiArrowLeft, FiCopy, FiExternalLink, FiKey, FiMinus, FiPlus, FiRefreshCw, FiSearch, FiXCircle,
} from 'react-icons/fi';
import { API_URLS, getTeamImageUrl } from '../constants/config';
import { useI18n } from '../utilities/i18n';
import { authorizedPost, getApiErrorMessage } from '../utilities/stadiumsApi';
import { useChampionships } from '../utilities/championships';
import { IfCanWrite, useAdminAccess } from '../utilities/adminAccess';
import {
  MATCH_PLAY_OPTIONS,
  extractMatch,
  extractMatchesPage,
  extractRoster,
  isOnFlag,
  matchPlayPhase,
  matchRequestBody,
  matchStatusKey,
  moscowTodayIso,
  playConditionForPhase,
  playerDisplayName,
  playerShirtNumber,
  scoreText,
  toEventTime,
} from '../utilities/matchesApi';
import { champFields } from '../utilities/tournamentsApi';
import TabloQuickPlayerMenu from '../components/TabloQuickPlayerMenu';
import OnlineClockBar from '../components/OnlineClockBar';
import {
  adoptServerClock,
  clockForPhase,
  clockMatchesServer,
  isClockLive,
  isClockRunning,
  makeClock,
  statusForPhase,
  timeNumberForStatus,
  useOnlineClock,
} from '../utilities/onlineMatchStore';
import {
  TABLO_DEFAULT_SCENE,
  TABLO_FALLBACK_SCENES,
  TABLO_SCENE_GROUPS,
  TABLO_SCORE_SCENE,
  buildTabloCustomBg,
  buildTabloOverlayPageUrl,
  closeTabloSession,
  extractTabloScenes,
  extractTabloSession,
  extractTabloState,
  extractTabloUnderlozhki,
  SCENE_TO_LAYER,
  getTabloLayers,
  getTabloState,
  getTabloStyles,
  isTabloClosedPayload,
  isTabloScoreScene,
  normalizeTabloScene,
  pickTabloScoreboardLayer,
  normalizeTabloSolidKey,
  resolveTabloEventPulses,
  resolveTabloPackScene,
  resolveTabloScoreVisible,
  resolveTabloSolidAccent,
  resolveTabloSolidTheme,
  rotateTabloOverlayKey,
  setTabloLeagueStyle,
  preferTabloClock,
  setTabloSession,
  subscribeTablo,
  TABLO_EVENT_CARD_MS,
  resolveTabloResultPulseMs,
} from '../utilities/tabloApi';
import { teamCardName } from '../utilities/matchStaffApi';
import StadiumCrumbs from '../components/StadiumCrumbs';
import TabloBoard from '../components/TabloBoard';
import defaultTeamLogo from '../images/defaultTeamLogo.png';
import '../styles/stadiumsManagement.css';
import '../styles/tablo.css';

const matchTeamName = (match, side) => {
  const team = side === 'home' ? match?.home_team : match?.guest_team;
  const flat = side === 'home'
    ? (match?.home_team_name || match?.home_team_short_name)
    : (match?.guest_team_name || match?.guest_team_short_name);
  return teamCardName(team) || team?.name || flat || '—';
};

const matchTeamFullName = (match, side) => {
  const team = side === 'home' ? match?.home_team : match?.guest_team;
  const flat = side === 'home'
    ? (match?.home_team_name || match?.home_team_short_name)
    : (match?.guest_team_name || match?.guest_team_short_name);
  return team?.name || flat || team?.short_name || '—';
};

const matchTeamImage = (match, side) => {
  const team = side === 'home' ? match?.home_team : match?.guest_team;
  const flat = side === 'home' ? match?.home_team_image : match?.guest_team_image;
  return getTeamImageUrl(team?.image || flat) || defaultTeamLogo;
};

const pad2 = (value) => String(value).padStart(2, '0');

const toIsoDate = (value) => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return moscowTodayIso();
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
};

export default function TabloPanel() {
  const dispatch = useDispatch();
  const { t, lang } = useI18n();
  const { champId } = useChampionships();
  const { canWrite } = useAdminAccess('matches');

  const [dateIso, setDateIso] = useState(() => moscowTodayIso());
  const [query, setQuery] = useState('');
  const [matches, setMatches] = useState([]);
  const [matchesLoading, setMatchesLoading] = useState(false);
  /** Полный админ-матч (settings.extra_time_available) — список табло его не отдаёт */
  const [adminMatch, setAdminMatch] = useState(null);
  const [matchId, setMatchId] = useState(null);
  const [scenes, setScenes] = useState(TABLO_FALLBACK_SCENES);
  const [underlozhki, setUnderlozhki] = useState([]);
  const [scene, setScene] = useState(TABLO_DEFAULT_SCENE);
  const [scoreVisible, setScoreVisible] = useState(true);
  const [theme, setTheme] = useState('blue');
  const [accent, setAccent] = useState('');
  const [session, setSession] = useState(null);
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState('');
  const [message, setMessage] = useState({ text: '', type: '' });
  const [live, setLive] = useState(false);
  const [eventVisible, setEventVisible] = useState(false);
  /** Авто-плашки событий (ЖК / гол → карточка / result). По умолчанию вкл. */
  const [eventPulses, setEventPulses] = useState(true);
  const eventPulsesRef = useRef(true);
  const [resultPulse, setResultPulse] = useState(false);
  const [roster, setRoster] = useState([]);
  const [liveMinute, setLiveMinute] = useState(0);
  const liveClockRef = useRef({ minute: 0, second: 0 });
  const [liveSecond, setLiveSecond] = useState(0);
  const [playerMenu, setPlayerMenu] = useState(null);
  const searchTimer = useRef(null);
  const matchesReq = useRef(0);
  const scoreVisibleRef = useRef(true);
  const eventTimer = useRef(null);
  const resultTimer = useRef(null);
  // Хуки таймера — сразу после state/ref, до эффектов с early-return
  // (иначе rules-of-hooks на билде считает вызов условным).
  const onlineClock = useOnlineClock(matchId);

  useEffect(() => {
    scoreVisibleRef.current = scoreVisible;
  }, [scoreVisible]);

  useEffect(() => {
    eventPulsesRef.current = eventPulses;
  }, [eventPulses]);

  // pulse:'event' → карточка ~6с; pulse:'result' → подробный результат ~6с.
  // Refresh без pulse (замена, фол, …) сцены не открывает и не обрывает.
  // event_pulses=false — авто-плашки не показываем.
  useEffect(() => {
    const pulsesOn = resolveTabloEventPulses(state, eventPulsesRef.current);

    if (state?.pulse === 'event') {
      if (!pulsesOn) {
        setEventVisible(false);
        setState((prev) => (
          prev && prev.pulse === 'event' ? { ...prev, pulse: null } : prev
        ));
        return undefined;
      }
      if (resultTimer.current) {
        clearTimeout(resultTimer.current);
        resultTimer.current = null;
      }
      if (eventTimer.current) {
        clearTimeout(eventTimer.current);
        eventTimer.current = null;
      }
      setResultPulse(false);
      setEventVisible(true);
      eventTimer.current = setTimeout(() => {
        eventTimer.current = null;
        setEventVisible(false);
        setState((prev) => (
          prev && prev.pulse === 'event' ? { ...prev, pulse: null } : prev
        ));
      }, TABLO_EVENT_CARD_MS);
      return () => {
        if (eventTimer.current) clearTimeout(eventTimer.current);
      };
    }

    if (state?.pulse === 'result') {
      if (!pulsesOn) {
        setResultPulse(false);
        setState((prev) => (
          prev && prev.pulse === 'result' ? { ...prev, pulse: null } : prev
        ));
        return undefined;
      }
      if (eventTimer.current) {
        clearTimeout(eventTimer.current);
        eventTimer.current = null;
      }
      if (resultTimer.current) {
        clearTimeout(resultTimer.current);
        resultTimer.current = null;
      }
      setEventVisible(false);
      setResultPulse(true);
      resultTimer.current = setTimeout(() => {
        resultTimer.current = null;
        setResultPulse(false);
        setState((prev) => (
          prev && prev.pulse === 'result' ? { ...prev, pulse: null } : prev
        ));
      }, resolveTabloResultPulseMs(state));
      return () => {
        if (resultTimer.current) clearTimeout(resultTimer.current);
      };
    }

    if (eventTimer.current || resultTimer.current) return undefined;
    setResultPulse(false);
    setEventVisible(normalizeTabloScene(scene) === 'event');
    return undefined;
  }, [state, scene]);

  const selectedMatch = useMemo(
    () => matches.find((item) => Number(item.id) === Number(matchId)) || state?.match || null,
    [matches, matchId, state]
  );

  /** Одна и та же сцена в нескольких группах — подсветка только в первой. */
  const activeSceneGroupId = useMemo(() => {
    if (!scene) return null;
    const group = TABLO_SCENE_GROUPS.find((item) => item.keys.includes(scene));
    return group ? group.id : null;
  }, [scene]);

  const rosterFromState = useMemo(() => {
    const apps = state && state.applications;
    if (!Array.isArray(apps) || !apps.length) return null;
    return extractRoster({ players: apps });
  }, [state]);

  const homePlayers = useMemo(() => {
    const activeMatchForRoster = (state && state.match) || selectedMatch;
    const effectiveRoster = (rosterFromState && rosterFromState.length) ? rosterFromState : roster;
    const teamId = Number(activeMatchForRoster && activeMatchForRoster.home_team_id);
    const onTeam = effectiveRoster.filter((p) => Number(p.team_id) === teamId);
    const active = onTeam.filter((p) => p.active);
    const list = active.length ? active : onTeam;
    return list
      .slice()
      .sort((a, b) => Number(playerShirtNumber(a) || 999) - Number(playerShirtNumber(b) || 999));
  }, [rosterFromState, roster, state, selectedMatch]);

  const guestPlayers = useMemo(() => {
    const activeMatchForRoster = (state && state.match) || selectedMatch;
    const effectiveRoster = (rosterFromState && rosterFromState.length) ? rosterFromState : roster;
    const teamId = Number(activeMatchForRoster && activeMatchForRoster.guest_team_id);
    const onTeam = effectiveRoster.filter((p) => Number(p.team_id) === teamId);
    const active = onTeam.filter((p) => p.active);
    const list = active.length ? active : onTeam;
    return list
      .slice()
      .sort((a, b) => Number(playerShirtNumber(a) || 999) - Number(playerShirtNumber(b) || 999));
  }, [rosterFromState, roster, state, selectedMatch]);

  // Живые минуты: тикаем локально. Серверный clock применяем только при
  // реальной смене minute/second/running — и не откатываем вперёд ушедший тик
  // (смена сцены присылает устаревший snapshot).
  const prevClockSyncRef = useRef({ minute: null, second: null, running: null });
  useEffect(() => {
    const clock = state && state.clock;
    if (!clock) {
      liveClockRef.current = { minute: 0, second: 0 };
      prevClockSyncRef.current = { minute: null, second: null, running: null };
      setLiveMinute(0);
      setLiveSecond(0);
      return undefined;
    }
    const nextMin = Number(clock.minute) || 0;
    const nextSec = Number(clock.second) || 0;
    const nextRunning = Boolean(clock.running);
    const prev = prevClockSyncRef.current;
    const clockChanged = prev.minute !== nextMin
      || prev.second !== nextSec
      || prev.running !== nextRunning;
    if (clockChanged) {
      const nextTotal = nextMin * 60 + nextSec;
      const localTotal = liveClockRef.current.minute * 60 + liveClockRef.current.second;
      // Сервер — источник истины; не принимаем только ложный 00:00 при уже идущем тике
      const falseZero = Boolean(nextRunning && nextTotal <= 1 && localTotal > 15);
      if (!falseZero) {
        liveClockRef.current = { minute: nextMin, second: nextSec };
        setLiveMinute(nextMin);
        setLiveSecond(nextSec);
      }
      prevClockSyncRef.current = { minute: nextMin, second: nextSec, running: nextRunning };
    }
    if (!nextRunning) return undefined;
    const timer = setInterval(() => {
      const cur = liveClockRef.current;
      let minute = cur.minute;
      let second = cur.second + 1;
      if (second >= 60) {
        second = 0;
        minute += 1;
      }
      liveClockRef.current = { minute, second };
      setLiveMinute(minute);
      setLiveSecond(second);
    }, 1000);
    return () => clearInterval(timer);
  }, [
    state && state.clock && state.clock.minute,
    state && state.clock && state.clock.second,
    state && state.clock && state.clock.running,
  ]);

  // ─── Общий таймер онлайна: паузы и правки (в мини-футболе стоп на каждом ауте) ───
  // Если в этом браузере идёт онлайн этого матча — часы и время событий берём из него.
  // Важно: state.match раньше selectedMatch — после «Идёт 1-й тайм» список ещё not_started,
  // иначе clockMatchesServer не сходится и кнопки паузы не появляются.
  const clockMatch = (state && state.match) || selectedMatch || null;
  const localClockLive = isClockLive(onlineClock.clock) && clockMatchesServer(onlineClock.clock, clockMatch);
  const clockPhase = matchPlayPhase(clockMatch);
  const serverClockRunning = Boolean(state && state.clock && state.clock.running);
  // Онлайн запускали не здесь — пауза начнётся от серверного времени.
  const baseClock = useMemo(() => {
    if (localClockLive) return null;
    const status = statusForPhase(clockPhase, serverClockRunning);
    if (!status || status === 'idle' || status === 'ended') return null;
    return makeClock(status, liveMinute * 60 + liveSecond);
  }, [localClockLive, clockPhase, serverClockRunning, liveMinute, liveSecond]);
  const shownSeconds = localClockLive ? onlineClock.seconds : liveMinute * 60 + liveSecond;
  const shownMinute = Math.floor(shownSeconds / 60);
  const shownSecond = shownSeconds % 60;

  // Таймер с сервера (паузы с другого устройства), когда бэкенд отдаёт match.live_clock.
  const serverLiveClockVersion = state && state.match && state.match.live_clock
    ? state.match.live_clock.updated_at
    : null;
  useEffect(() => {
    if (matchId && state && state.match && state.match.live_clock) adoptServerClock(matchId, state.match);
  }, [matchId, serverLiveClockVersion]); // eslint-disable-line react-hooks/exhaustive-deps

  // Заявка матча (fallback, если в state.applications пусто)
  useEffect(() => {
    const rosterMatch = (state && state.match) || selectedMatch;
    const homeId = rosterMatch && rosterMatch.home_team_id;
    const guestId = rosterMatch && rosterMatch.guest_team_id;
    if (!matchId || !homeId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const response = await authorizedPost(dispatch, API_URLS.getRosterEvents, {
          match_id: matchId,
          team_id: [homeId, guestId],
          with_doubles: true,
        });
        if (cancelled) return;
        setRoster(extractRoster(response.data));
      } catch (_err) {
        if (!cancelled) setRoster([]);
      }
    })();
    return () => { cancelled = true; };
  }, [dispatch, matchId, state, selectedMatch]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await getTabloStyles(dispatch);
        if (cancelled) return;
        setScenes(extractTabloScenes(response.data));
        const nextUnders = extractTabloUnderlozhki(response.data);
        setUnderlozhki(nextUnders);
        const solid = resolveTabloSolidTheme(theme, nextUnders);
        setTheme(solid?.key || 'blue');
        setAccent((prev) => resolveTabloSolidAccent(
          { theme: solid?.key, accent: prev },
          nextUnders
        ));
      } catch (_err) {
        if (!cancelled) {
          setScenes(TABLO_FALLBACK_SCENES.map((item) => ({ ...item })));
          const fallbackUnders = extractTabloUnderlozhki({});
          setUnderlozhki(fallbackUnders);
          const solid = resolveTabloSolidTheme(theme, fallbackUnders);
          setTheme(solid?.key || 'blue');
          setAccent((prev) => resolveTabloSolidAccent(
            { theme: solid?.key, accent: prev },
            fallbackUnders
          ));
        }
      }
    })();
    return () => { cancelled = true; };
  }, [dispatch]);

  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      loadMatches();
    }, 250);
    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    };
  }, [champId, dateIso, query]);

  useEffect(() => {
    if (!matchId) {
      setAdminMatch(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const response = await authorizedPost(dispatch, API_URLS.getAdminMatch, {
          id: matchId,
          ...champFields(champId),
        });
        if (cancelled) return;
        setAdminMatch(extractMatch(response.data) || null);
      } catch (_err) {
        if (!cancelled) setAdminMatch(null);
      }
    })();
    return () => { cancelled = true; };
  }, [matchId, champId, dispatch]);

  useEffect(() => {
    if (!matchId) {
      setSession(null);
      setState(null);
      setLive(false);
      setScene(TABLO_DEFAULT_SCENE);
      setScoreVisible(true);
      setTheme('blue');
      setAccent('');
      setRoster([]);
      setPlayerMenu(null);
      liveClockRef.current = { minute: 0, second: 0 };
      setLiveMinute(0);
      setLiveSecond(0);
      setAdminMatch(null);
      return undefined;
    }
    // Сразу превью + табло (always_base); табло в админке рисуется поверх превью
    setScene(TABLO_DEFAULT_SCENE);
    setScoreVisible(true);
    let cancelled = false;
    (async () => {
      try {
        const response = await getTabloState(dispatch, matchId);
        if (cancelled) return;
        const nextState = extractTabloState(response.data);
        const nextSession = response.data?.session_active === false
          ? null
          : extractTabloSession(response.data?.session || response.data);
        const hasSession = Boolean(nextSession?.overlay_key);
        const nextScene = TABLO_DEFAULT_SCENE;
        const nextScoreVisible = hasSession
          ? resolveTabloScoreVisible(
            nextState,
            isTabloScoreScene(nextState?.scene) || true
          )
          : true;

        // Схема из сессии или сохранённая для лиги+сезона (solid theme/accent).
        const rawThemeKey = nextState?.style?.theme
          || nextState?.style?.underlozhka
          || 'blue';
        const solid = resolveTabloSolidTheme(rawThemeKey, underlozhki);
        const nextTheme = solid?.key || 'blue';
        const nextAccent = resolveTabloSolidAccent(
          { theme: nextTheme, accent: nextState?.style?.accent },
          underlozhki,
          { forceSolid: true }
        );
        const nextEventPulses = resolveTabloEventPulses(nextState, true);
        setTheme(nextTheme);
        setAccent(nextAccent);
        setEventPulses(nextEventPulses);
        eventPulsesRef.current = nextEventPulses;
        setSession(hasSession ? nextSession : null);
        setScene(nextScene);
        setScoreVisible(nextScoreVisible);

        const solidCustomBg = buildTabloCustomBg({
          theme: nextTheme,
          accent: nextAccent,
          stadiumBackground: solid?.backgroundColor,
        });

        if (!hasSession) {
          // Пересобираем слои solid-цветом — getTabloState отдаёт старую pattern-подложку лиги
          try {
            const layersResponse = await getTabloLayers(dispatch, {
              matchId,
              scene: nextScene,
              scoreboard: nextScoreVisible,
              customBg: solidCustomBg,
            });
            if (cancelled) return;
            const layered = extractTabloState(layersResponse.data);
            // Только новые слои — не мержим html старой подложки из getTabloState
            const mergedLayers = mergeScoreboardFromPayload(
              layersResponse.data,
              layered?.layers || {}
            );
            setState({
              ...(nextState || {}),
              ...(layered || {}),
              match: layered?.match || nextState?.match || null,
              scene: nextScene,
              picture_type: nextScene,
              scoreboard_visible: nextScoreVisible,
              style: {
                theme: nextTheme,
                underlozhka: nextTheme,
                accent: nextAccent,
                backgroundColor: solid?.backgroundColor,
              },
              clock: preferTabloClock(
                (layered && layered.clock) || (nextState && nextState.clock),
                null
              ),
              layers: mergedLayers,
              layer_urls: layered?.layer_urls || {},
              closed: false,
            });
          } catch (_layersErr) {
            if (cancelled) return;
            setState(nextState ? {
              ...nextState,
              scene: nextScene,
              picture_type: nextScene,
              scoreboard_visible: nextScoreVisible,
              style: {
                theme: nextTheme,
                underlozhka: nextTheme,
                accent: nextAccent,
                backgroundColor: solid?.backgroundColor,
              },
              layers: {},
            } : nextState);
          }
        } else {
          const rawScene = nextState?.scene || nextSession?.scene;
          const packScene = rawScene ? resolveTabloPackScene(rawScene) : nextScene;
          setScene(packScene);
          // Сессия могла сохранить pattern — пересобираем слои solid customBg
          try {
            const layersResponse = await getTabloLayers(dispatch, {
              matchId,
              scene: packScene || nextScene,
              scoreboard: nextScoreVisible,
              customBg: solidCustomBg,
            });
            if (cancelled) return;
            const layered = extractTabloState(layersResponse.data);
            const mergedLayers = mergeScoreboardFromPayload(
              layersResponse.data,
              mergeTabloLayers(nextState?.layers, layered?.layers)
            );
            setState(nextState ? {
              ...nextState,
              ...(layered || {}),
              match: layered?.match || nextState?.match || null,
              scene: packScene,
              picture_type: packScene,
              scoreboard_visible: nextScoreVisible,
              style: {
                theme: nextTheme,
                underlozhka: nextTheme,
                accent: nextAccent,
                backgroundColor: solid?.backgroundColor,
              },
              clock: preferTabloClock(
                (layered && layered.clock) || nextState.clock,
                null
              ),
              layers: mergedLayers,
              layer_urls: {
                ...(nextState?.layer_urls || {}),
                ...(layered?.layer_urls || {}),
              },
            } : nextState);
          } catch (_layersErr) {
            if (cancelled) return;
            setState(nextState ? {
              ...nextState,
              scene: packScene,
              picture_type: packScene,
              scoreboard_visible: nextScoreVisible,
              style: {
                theme: nextTheme,
                underlozhka: nextTheme,
                accent: nextAccent,
                backgroundColor: solid?.backgroundColor,
              },
            } : nextState);
          }
        }
      } catch (_err) {
        if (!cancelled) {
          setSession(null);
          setScene(TABLO_DEFAULT_SCENE);
          setScoreVisible(true);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [matchId, dispatch]);

  useEffect(() => {
    if (!session?.overlay_key && !matchId) return undefined;
    const liveSession = Boolean(session?.overlay_key);
    return subscribeTablo({
      overlayKey: session?.overlay_key || undefined,
      matchId: matchId || undefined,
      onUpdate: (next) => {
        if (isTabloClosedPayload(next) && next.reason === 'closed') {
          setSession(null);
          setState(next);
          setLive(false);
          return;
        }
        if (isTabloClosedPayload(next) && next.reason === 'rotated') return;
        setState((prev) => {
          const nextPack = liveSession
            ? resolveTabloPackScene(next?.scene || prev?.scene)
            : resolveTabloPackScene(
              prev && Object.prototype.hasOwnProperty.call(prev, 'scene')
                ? prev.scene
                : TABLO_DEFAULT_SCENE
            );
          const nextScore = liveSession
            ? resolveTabloScoreVisible(
              next,
              isTabloScoreScene(next?.scene)
                ? true
                : resolveTabloScoreVisible(prev, false)
            )
            : resolveTabloScoreVisible(prev, false);
          return {
            ...(prev || {}),
            ...next,
            // Без эфира: joinTablo отдаёт стиль лиги (*_long + белый accent) —
            // не трогаем локальные solid-слои/стиль, иначе превью залипает на старой подложке.
            scene: nextPack,
            picture_type: nextPack,
            scoreboard_visible: nextScore,
            style: liveSession
              ? (next?.style || prev?.style)
              : (prev?.style || null),
            layers: liveSession
              ? mergeTabloLayers(prev?.layers, next?.layers)
              : (prev?.layers || {}),
            layer_urls: liveSession
              ? {
                ...(prev?.layer_urls || {}),
                ...(next?.layer_urls || {}),
              }
              : (prev?.layer_urls || {}),
            match: next?.match || prev?.match || null,
            clock: preferTabloClock(next?.clock, prev?.clock),
            closed: false,
          };
        });
        setLive(true);
        if (liveSession) {
          if (next?.scene) setScene(resolveTabloPackScene(next.scene));
          if (typeof next?.scoreboard_visible === 'boolean'
            || typeof next?.scoreboard === 'boolean'
            || typeof next?.scoreboard_on === 'boolean'
            || typeof next?.style?.scoreboard_visible === 'boolean') {
            setScoreVisible((prevScore) => resolveTabloScoreVisible(next, prevScore));
          }
          if (typeof next?.event_pulses === 'boolean'
            || typeof next?.style?.event_pulses === 'boolean') {
            const nextPulses = resolveTabloEventPulses(next, true);
            setEventPulses(nextPulses);
            eventPulsesRef.current = nextPulses;
          }
          if (next?.style) syncThemeFromStyle(next.style);
        }
        if (next?.overlay_key) {
          setSession((prev) => ({
            ...(prev || {}),
            match_id: next.match_id || matchId,
            overlay_key: next.overlay_key,
            scene: resolveTabloPackScene(next.scene || prev?.scene),
          }));
        }
      },
      onAck: (ack) => setLive(Boolean(ack?.ok)),
    });
  }, [session, matchId]);

  // Производные значения — только после всех хуков (иначе rules-of-hooks / optional chaining)
  const overlayPageUrl = (session && session.overlay_key)
    ? buildTabloOverlayPageUrl(session.overlay_key)
    : '';
  const activeMatchForRoster = (state && state.match) || selectedMatch;
  const effectiveRoster = (rosterFromState && rosterFromState.length) ? rosterFromState : roster;
  const matchEvents = (state && Array.isArray(state.events)) ? state.events : [];
  const phase = matchPlayPhase((state && state.match) || selectedMatch);
  const extraTimeAvailable = isOnFlag(
    (adminMatch && adminMatch.settings && adminMatch.settings.extra_time_available)
      || (adminMatch && adminMatch.extra_time_available)
      || (state && state.match && state.match.settings && state.match.settings.extra_time_available)
      || (state && state.match && state.match.extra_time_available)
      || (selectedMatch && selectedMatch.settings && selectedMatch.settings.extra_time_available)
      || (selectedMatch && selectedMatch.extra_time_available)
  );
  const playOptions = MATCH_PLAY_OPTIONS.filter((item) => {
    if (item.value === 'tech_defeat') return false;
    if (item.extraOnly) return extraTimeAvailable || phase === item.value;
    return true;
  });
  const selectedUnder = underlozhki.find((item) => item.key === theme) || underlozhki[0];
  const styleSource = (state && state.style_source && state.style_source.match) || '';

  const showMsg = (text, type = 'success') => setMessage({ text, type });

  const syncThemeFromStyle = (style) => {
    if (!style || typeof style !== 'object') return;
    const solid = resolveTabloSolidTheme(style.theme || style.underlozhka, underlozhki);
    if (solid?.key) setTheme(solid.key);
    // forceSolid, если исходный ключ был pattern/пустой (не уже solid)
    const rawKey = String(style.theme || style.underlozhka || '').trim().toLowerCase();
    const forceSolid = normalizeTabloSolidKey(rawKey) !== rawKey;
    setAccent(resolveTabloSolidAccent(
      { ...style, theme: solid?.key },
      underlozhki,
      { forceSolid }
    ));
  };

  const currentCustomBg = (clear = false, overrides = {}) => {
    if (clear) return null;
    const nextTheme = overrides.theme || theme || selectedUnder?.key || 'blue';
    const under = underlozhki.find((item) => item.key === nextTheme) || selectedUnder;
    const nextPulses = typeof overrides.eventPulses === 'boolean'
      ? overrides.eventPulses
      : eventPulsesRef.current;
    return buildTabloCustomBg({
      theme: nextTheme,
      accent: overrides.accent || accent || under?.accent || undefined,
      stadiumBackground: overrides.stadiumBackground || under?.backgroundColor || undefined,
      eventPulses: nextPulses,
    });
  };

  const mergeTabloLayers = (prevLayers, nextLayers) => {
    if (!nextLayers || !Object.keys(nextLayers).length) return prevLayers || {};
    const merged = { ...(prevLayers || {}) };
    Object.keys(nextLayers).forEach((key) => {
      const next = nextLayers[key];
      if (!next) return;
      // Новый html/url побеждает; иначе оставляем предыдущий кадр
      if (next.html || next.url) merged[key] = next;
      else if (!merged[key]) merged[key] = next;
    });
    return merged;
  };

  const mergeScoreboardFromPayload = (payload, prevLayers = {}) => {
    const picked = pickTabloScoreboardLayer(payload);
    const extracted = extractTabloState(payload);
    const fromExtract = extracted?.layers?.scoreboard;
    const scoreboard = (picked?.html || picked?.url)
      ? picked
      : ((fromExtract?.html || fromExtract?.url) ? fromExtract : null);
    if (!scoreboard) return prevLayers || {};
    return mergeTabloLayers(prevLayers, { scoreboard });
  };

  /** Всегда добираем layers.scoreboard — pack-ответ его часто не содержит. */
  const fetchScoreboardLayer = async (customBg) => {
    const response = await getTabloLayers(dispatch, {
      matchId,
      overlayKey: session?.overlay_key || undefined,
      scene: TABLO_SCORE_SCENE,
      scoreboard: true,
      customBg,
    });
    return response;
  };

  const applySessionResponse = (response, fallbackScene = scene, fallbackScore = scoreVisible) => {
    const nextSession = extractTabloSession(response.data);
    const nextState = extractTabloState(response.data);
    const packScene = resolveTabloPackScene(
      nextState?.scene || nextSession?.scene || fallbackScene
    );
    const nextScore = resolveTabloScoreVisible(
      nextState || nextSession,
      isTabloScoreScene(nextState?.scene) ? true : fallbackScore
    );
    if (nextSession?.overlay_key) setSession(nextSession);
    setScoreVisible(nextScore);
    setState((prev) => ({
      ...(prev || {}),
      ...(nextState || {}),
      scene: packScene,
      picture_type: packScene,
      scoreboard_visible: nextScore,
      match: nextState?.match || prev?.match || null,
      style: nextState?.style || prev?.style || {},
      clock: preferTabloClock(nextState?.clock, prev?.clock),
      events: nextState?.events || prev?.events || [],
      active_event: nextState?.active_event || null,
      lineups: nextState?.lineups || prev?.lineups || {},
      applications: nextState?.applications || prev?.applications || [],
      statistics: nextState?.statistics || prev?.statistics || [],
      layers: mergeTabloLayers(prev?.layers, nextState?.layers),
      layer_urls: {
        ...(prev?.layer_urls || {}),
        ...(nextState?.layer_urls || {}),
      },
      pulse: nextState?.pulse || null,
      closed: false,
    }));
    if (nextState?.style) syncThemeFromStyle(nextState.style);
    setScene(packScene);
  };

  /** После смены стиля — полный snapshot, чтобы подтянуть все layers, не только текущую сцену. */
  const refreshTabloState = async (fallbackScene = scene) => {
    if (!matchId) return;
    try {
      const response = await getTabloState(dispatch, matchId);
      applySessionResponse(response, fallbackScene);
    } catch (_err) {
      // ignore
    }
  };

  const loadMatches = async () => {
    if (!champId) {
      setMatches([]);
      return;
    }
    const reqId = ++matchesReq.current;
    setMatchesLoading(true);
    try {
      const response = await authorizedPost(
        dispatch,
        API_URLS.getAdminMatches,
        matchRequestBody(champId, {
          date: dateIso || undefined,
          search: query.trim() || undefined,
          limit: 40,
          offset: 0,
        })
      );
      if (reqId !== matchesReq.current) return;
      setMatches(extractMatchesPage(response.data).matches || []);
    } catch (error) {
      if (reqId !== matchesReq.current) return;
      setMatches([]);
      showMsg(getApiErrorMessage(error, t('tabloMatchesError')), 'error');
    } finally {
      if (reqId === matchesReq.current) setMatchesLoading(false);
    }
  };

  const handleCreateSession = async ({
    nextScene = scene,
    nextScoreVisible = scoreVisible,
    withStyle = true,
    styleOverrides = {},
    quiet = false,
  } = {}) => {
    if (!matchId) {
      showMsg(t('tabloPickMatch'), 'error');
      return;
    }
    const packScene = resolveTabloPackScene(nextScene);
    setBusy(quiet ? 'style' : 'session');
    try {
      const response = await setTabloSession(dispatch, {
        matchId,
        scene: packScene,
        scoreboard: nextScoreVisible,
        customBg: withStyle ? currentCustomBg(false, styleOverrides) : undefined,
      });
      applySessionResponse(response, packScene, nextScoreVisible);
      // Стиль влияет на все плашки пакета — добираем полный state
      if (withStyle) await refreshTabloState(packScene);
    } catch (error) {
      showMsg(getApiErrorMessage(error, t('tabloSessionError')), 'error');
    } finally {
      setBusy('');
    }
  };

  /** Без эфира: запомнить solid-схему для лиги+сезона и обновить превью. */
  const persistLeagueStyleAndPreview = async (styleOverrides = {}) => {
    const customBg = currentCustomBg(false, styleOverrides);
    await setTabloLeagueStyle(dispatch, { matchId, customBg });
    await refreshPreviewWithoutSession({ styleOverrides, force: true });
  };

  const applyPreviewLayers = (response, nextScene = scene, styleOverrides = {}, nextScore = scoreVisible) => {
    const payload = response?.data || response;
    const nextState = extractTabloState(payload);
    const packScene = resolveTabloPackScene(nextScene);
    const nextTheme = styleOverrides.theme || theme;
    const nextAccent = styleOverrides.accent !== undefined ? styleOverrides.accent : accent;
    setState((prev) => {
      const mergedLayers = mergeTabloLayers(prev?.layers, nextState?.layers);
      return {
        ...(prev || {}),
        ...(nextState || {}),
        scene: packScene,
        picture_type: packScene,
        scoreboard_visible: nextScore,
        style: {
          ...(prev?.style || {}),
          ...(nextState?.style || {}),
          theme: nextTheme,
          underlozhka: nextTheme,
          accent: nextAccent || nextState?.style?.accent || prev?.style?.accent,
        },
        clock: preferTabloClock(nextState?.clock, prev?.clock),
        // getTabloLayers всегда отдаёт все слои, включая scoreboard — явно мержим
        layers: mergeScoreboardFromPayload(payload, mergedLayers),
        layer_urls: {
          ...(prev?.layer_urls || {}),
          ...(nextState?.layer_urls || {}),
        },
        closed: false,
      };
    });
  };

  const refreshPreviewWithoutSession = async ({
    nextScene = scene,
    nextScoreVisible = scoreVisible,
    styleOverrides = {},
    force = false,
  } = {}) => {
    if (!matchId) return;
    const packScene = resolveTabloPackScene(nextScene);
    if (!packScene) {
      setState((prev) => ({
        ...(prev || {}),
        scene: null,
        picture_type: null,
        scoreboard_visible: nextScoreVisible,
        closed: false,
      }));
      if (!nextScoreVisible) return;
      const hasScoreLayer = Boolean(
        state?.layers?.scoreboard?.html
        || state?.layer_urls?.scoreboard
      );
      if (!force && hasScoreLayer) return;
      try {
        const scoreResponse = await fetchScoreboardLayer(currentCustomBg(false, styleOverrides));
        const scoreUrls = extractTabloState(scoreResponse.data)?.layer_urls || {};
        setState((prev) => ({
          ...(prev || {}),
          scene: null,
          picture_type: null,
          scoreboard_visible: true,
          layers: mergeScoreboardFromPayload(scoreResponse.data, prev?.layers),
          layer_urls: {
            ...(prev?.layer_urls || {}),
            ...(scoreUrls.scoreboard ? { scoreboard: scoreUrls.scoreboard } : {}),
          },
          closed: false,
        }));
      } catch (_scoreErr) {
        // кадр сцены уже снят
      }
      return;
    }
    const layerKey = SCENE_TO_LAYER[packScene] || 'preview';
    const hasPackLayer = Boolean(
      state?.layers?.[layerKey]?.html
      || state?.layer_urls?.[layerKey]
    );
    const hasScoreLayer = Boolean(
      state?.layers?.scoreboard?.html
      || state?.layer_urls?.scoreboard
    );
    if (
      !force
      && hasPackLayer
      && (!nextScoreVisible || hasScoreLayer)
      && !styleOverrides.theme
      && styleOverrides.accent === undefined
    ) {
      setState((prev) => ({
        ...(prev || {}),
        scene: packScene,
        picture_type: packScene,
        scoreboard_visible: nextScoreVisible,
        closed: false,
      }));
      return;
    }
    const customBg = currentCustomBg(false, styleOverrides);
    const response = await getTabloLayers(dispatch, {
      matchId,
      scene: packScene,
      scoreboard: nextScoreVisible,
      customBg,
    });
    applyPreviewLayers(response, packScene, styleOverrides, nextScoreVisible);

    // Scoreboard всегда отдельным запросом — иначе с result/lineups его нет в state
    if (nextScoreVisible) {
      try {
        const scoreResponse = await fetchScoreboardLayer(customBg);
        const scoreUrls = extractTabloState(scoreResponse.data)?.layer_urls || {};
        setState((prev) => ({
          ...(prev || {}),
          scene: packScene,
          picture_type: packScene,
          scoreboard_visible: true,
          layers: mergeScoreboardFromPayload(scoreResponse.data, prev?.layers),
          layer_urls: {
            ...(prev?.layer_urls || {}),
            ...(scoreUrls.scoreboard ? { scoreboard: scoreUrls.scoreboard } : {}),
          },
          closed: false,
        }));
      } catch (_scoreErr) {
        // pack-превью уже применено
      }
    }
  };

  const handleUnderlozhkaClick = async (item) => {
    if (!item?.key || !matchId) return;
    setTheme(item.key);
    const nextAccent = item.accent || accent || '';
    if (item.accent) setAccent(item.accent);
    const styleOverrides = {
      theme: item.key,
      accent: nextAccent,
      stadiumBackground: item.backgroundColor,
    };
    // Без эфира — пишем схему лиги+сезона, сессию не открываем
    if (!session?.overlay_key) {
      if (!canWrite) {
        setBusy('style');
        try {
          await refreshPreviewWithoutSession({ styleOverrides, force: true });
        } catch (error) {
          showMsg(getApiErrorMessage(error, t('tabloSessionError')), 'error');
        } finally {
          setBusy('');
        }
        return;
      }
      setBusy('style');
      try {
        await persistLeagueStyleAndPreview(styleOverrides);
      } catch (error) {
        showMsg(getApiErrorMessage(error, t('tabloSessionError')), 'error');
      } finally {
        setBusy('');
      }
      return;
    }
    if (!canWrite) return;
    await handleCreateSession({
      withStyle: true,
      styleOverrides,
      quiet: true,
    });
  };

  const handleAccentCommit = async (nextAccent) => {
    const value = String(nextAccent || '').trim();
    setAccent(value);
    if (!matchId) return;
    if (!session?.overlay_key) {
      if (!canWrite) {
        setBusy('style');
        try {
          await refreshPreviewWithoutSession({
            styleOverrides: { accent: value || undefined },
            force: true,
          });
        } catch (error) {
          showMsg(getApiErrorMessage(error, t('tabloSessionError')), 'error');
        } finally {
          setBusy('');
        }
        return;
      }
      setBusy('style');
      try {
        await persistLeagueStyleAndPreview({ accent: value || undefined });
      } catch (error) {
        showMsg(getApiErrorMessage(error, t('tabloSessionError')), 'error');
      } finally {
        setBusy('');
      }
      return;
    }
    if (!canWrite) return;
    await handleCreateSession({
      withStyle: true,
      styleOverrides: { accent: value || undefined },
      quiet: true,
    });
  };

  const applyLocalScene = (key, patch = {}) => {
    const packScene = resolveTabloPackScene(key);
    setScene(packScene);
    setState((prev) => ({
      ...(prev || {}),
      ...patch,
      scene: packScene,
      picture_type: packScene,
      scoreboard_visible: patch.scoreboard_visible !== undefined
        ? patch.scoreboard_visible
        : (prev?.scoreboard_visible ?? scoreVisible),
      closed: false,
    }));
  };

  const handleEventPulsesToggle = async () => {
    const next = !eventPulsesRef.current;
    eventPulsesRef.current = next;
    setEventPulses(next);
    setState((prev) => ({
      ...(prev || {}),
      event_pulses: next,
      style: {
        ...(prev?.style || {}),
        event_pulses: next,
      },
      closed: false,
    }));
    if (!next) {
      setEventVisible(false);
      setResultPulse(false);
      if (eventTimer.current) {
        clearTimeout(eventTimer.current);
        eventTimer.current = null;
      }
      if (resultTimer.current) {
        clearTimeout(resultTimer.current);
        resultTimer.current = null;
      }
    }
    if (!session?.overlay_key || !canWrite) return;
    setBusy('style');
    try {
      await setTabloSession(dispatch, {
        matchId,
        scene,
        scoreboard: scoreVisibleRef.current,
        customBg: currentCustomBg(false, { eventPulses: next }),
      });
    } catch (error) {
      showMsg(getApiErrorMessage(error, t('tabloSessionError')), 'error');
      eventPulsesRef.current = !next;
      setEventPulses(!next);
    } finally {
      setBusy('');
    }
  };

  const handleScoreToggle = async () => {
    const nextScore = !scoreVisibleRef.current;
    scoreVisibleRef.current = nextScore;
    setScoreVisible(nextScore);
    setState((prev) => ({
      ...(prev || {}),
      scoreboard_visible: nextScore,
      closed: false,
    }));

    if (!session?.overlay_key) {
      setBusy('scene');
      try {
        await refreshPreviewWithoutSession({
          nextScene: scene,
          nextScoreVisible: nextScore,
          force: true,
        });
      } catch (error) {
        showMsg(getApiErrorMessage(error, t('tabloSessionError')), 'error');
        scoreVisibleRef.current = !nextScore;
        setScoreVisible(!nextScore);
      } finally {
        setBusy('');
      }
      return;
    }
    if (!canWrite) return;
    setBusy('scene');
    try {
      const customBg = currentCustomBg();
      const response = await setTabloSession(dispatch, {
        matchId,
        scene,
        scoreboard: nextScore,
        customBg,
      });
      applySessionResponse(response, scene, nextScore);
      if (nextScore) {
        try {
          const scoreResponse = await fetchScoreboardLayer(customBg);
          const scoreUrls = extractTabloState(scoreResponse.data)?.layer_urls || {};
          setState((prev) => ({
            ...(prev || {}),
            scoreboard_visible: true,
            layers: mergeScoreboardFromPayload(scoreResponse.data, prev?.layers),
            layer_urls: {
              ...(prev?.layer_urls || {}),
              ...(scoreUrls.scoreboard ? { scoreboard: scoreUrls.scoreboard } : {}),
            },
            closed: false,
          }));
        } catch (_scoreErr) {
          // session уже применена
        }
      }
    } catch (error) {
      showMsg(getApiErrorMessage(error, t('tabloSessionError')), 'error');
      scoreVisibleRef.current = !nextScore;
      setScoreVisible(!nextScore);
    } finally {
      setBusy('');
    }
  };

  const handleSceneClick = async (nextScene) => {
    const key = normalizeTabloScene(nextScene);
    if (key === TABLO_SCORE_SCENE || isTabloScoreScene(key)) {
      await handleScoreToggle();
      return;
    }
    const packScene = scene === key ? null : key;
    const scoreOn = scoreVisibleRef.current;
    // Без сессии — только локальный превью-переключатель, эфир не открываем
    if (!session?.overlay_key) {
      applyLocalScene(packScene, { scoreboard_visible: scoreOn });
      setBusy('scene');
      try {
        await refreshPreviewWithoutSession({
          nextScene: packScene,
          nextScoreVisible: scoreOn,
          force: true,
        });
      } catch (error) {
        showMsg(getApiErrorMessage(error, t('tabloSessionError')), 'error');
      } finally {
        setBusy('');
      }
      return;
    }
    if (!canWrite) {
      applyLocalScene(packScene, { scoreboard_visible: scoreOn });
      return;
    }
    setBusy('scene');
    try {
      const customBg = currentCustomBg();
      // customBg на смене сцены — чтобы слой пересобрался с текущим цветом
      const response = await setTabloSession(dispatch, {
        matchId,
        scene: packScene,
        scoreboard: scoreOn,
        customBg,
      });
      applySessionResponse(response, packScene, scoreOn);
      if (scoreOn) {
        try {
          const scoreResponse = await fetchScoreboardLayer(customBg);
          const scoreUrls = extractTabloState(scoreResponse.data)?.layer_urls || {};
          setState((prev) => ({
            ...(prev || {}),
            scene: packScene,
            picture_type: packScene,
            scoreboard_visible: true,
            layers: mergeScoreboardFromPayload(scoreResponse.data, prev?.layers),
            layer_urls: {
              ...(prev?.layer_urls || {}),
              ...(scoreUrls.scoreboard ? { scoreboard: scoreUrls.scoreboard } : {}),
            },
            closed: false,
          }));
        } catch (_scoreErr) {
          // pack уже применён
        }
      }
    } catch (error) {
      showMsg(getApiErrorMessage(error, t('tabloSessionError')), 'error');
    } finally {
      setBusy('');
    }
  };

  const handleCloseSession = async () => {
    if (!matchId || !canWrite) return;
    if (!window.confirm(t('tabloCloseConfirm'))) return;
    setBusy('close');
    try {
      await closeTabloSession(dispatch, matchId);
      setSession(null);
      setState({ closed: true, reason: 'closed', match_id: matchId });
      setLive(false);
    } catch (error) {
      showMsg(getApiErrorMessage(error, t('tabloCloseError')), 'error');
    } finally {
      setBusy('');
    }
  };

  const handleRotateKey = async () => {
    if (!matchId || !canWrite) return;
    if (!window.confirm(t('tabloRotateConfirm'))) return;
    setBusy('rotate');
    try {
      const response = await rotateTabloOverlayKey(dispatch, matchId);
      applySessionResponse(response);
      showMsg(t('tabloRotatedOk'));
    } catch (error) {
      showMsg(getApiErrorMessage(error, t('tabloRotateError')), 'error');
    } finally {
      setBusy('');
    }
  };

  const copyOverlayUrl = async () => {
    if (!overlayPageUrl) return;
    try {
      await navigator.clipboard.writeText(overlayPageUrl);
      showMsg(t('tabloUrlCopied'));
    } catch (_err) {
      showMsg(overlayPageUrl, 'success');
    }
  };

  const openOverlay = () => {
    if (!overlayPageUrl) return;
    window.open(overlayPageUrl, '_blank', 'noopener,noreferrer');
  };

  const bumpScore = async (side, delta) => {
    const match = state?.match || selectedMatch;
    if (!match?.id || !canWrite) return;
    const home = Number(match.home_team_scores) || 0;
    const guest = Number(match.guest_team_scores) || 0;
    setBusy('score');
    try {
      const response = await authorizedPost(dispatch, API_URLS.setAdminMatchScore, {
        match_id: Number(match.id),
        home_team_scores: side === 'home' ? Math.max(0, home + delta) : home,
        guest_team_scores: side === 'guest' ? Math.max(0, guest + delta) : guest,
      });
      const next = extractMatch(response.data);
      if (next) {
        setState((prev) => ({
          ...(prev || {}),
          match_id: next.id,
          match: { ...(prev?.match || {}), ...next },
          closed: false,
        }));
      }
    } catch (error) {
      showMsg(getApiErrorMessage(error, t('gmSaveError')), 'error');
    } finally {
      setBusy('');
    }
  };

  const setPlay = async (nextPhase) => {
    const match = state?.match || selectedMatch;
    if (!match?.id || !canWrite) return;
    const condition = playConditionForPhase(nextPhase, phase, match);
    setBusy('play');
    try {
      const response = await authorizedPost(dispatch, API_URLS.setAdminMatchPlayState, {
        match_id: Number(match.id),
        condition,
        team_id: match.home_team_id,
      });
      const next = extractMatch(response.data);
      // Фаза для clockMatchesServer / кнопок паузы. Список матчей ещё not_started —
      // без маркеров в state.match кнопки таймера не появятся.
      const phasePatch = (() => {
        const now = new Date().toISOString();
        if (nextPhase === 'first_half') {
          return { status: 1, start_in: next?.start_in || match.start_in || now };
        }
        if (nextPhase === 'half_time') {
          return { status: 0, start_in: next?.start_in || match.start_in || now, second_time_start: null };
        }
        if (nextPhase === 'second_half') {
          return {
            status: 1,
            start_in: next?.start_in || match.start_in || now,
            second_time_start: next?.second_time_start || match.second_time_start || now,
          };
        }
        if (nextPhase === 'finished' || nextPhase === 'extra_ended' || nextPhase === 'tech_defeat') {
          return { status: nextPhase === 'tech_defeat' ? 5 : 2 };
        }
        return {};
      })();
      const mergedMatch = { ...(match || {}), ...(next || {}), ...phasePatch };
      setState((prev) => ({
        ...(prev || {}),
        match_id: mergedMatch.id || match.id,
        match: { ...(prev?.match || {}), ...mergedMatch },
        closed: false,
      }));
      // Общий таймер онлайна — на начало нового этапа (как кнопки этапов в окне онлайна).
      if (nextPhase !== phase) {
        const nextClock = clockForPhase(nextPhase, {
          halfTime: match.half_time,
          extraHalfMin: match?.settings?.extra_half_time || match?.extra_half_time,
          currentSeconds: shownSeconds,
        });
        if (nextClock) onlineClock.setClock(nextClock);
      }
      // Сразу подтянуть clock / live_clock (перерыв → half_time; паузы с сервера)
      try {
        const tabloRes = await getTabloState(dispatch, Number(match.id));
        const tabloState = extractTabloState(tabloRes.data);
        if (tabloState) {
          const nextMatch = {
            ...(match || {}),
            ...(tabloState.match || {}),
            ...(next || {}),
            ...phasePatch,
          };
          setState((prev) => ({
            ...(prev || {}),
            clock: preferTabloClock(tabloState.clock, prev?.clock),
            match: { ...(prev?.match || {}), ...nextMatch },
          }));
          if (nextMatch.live_clock) adoptServerClock(Number(match.id), nextMatch);
        }
      } catch (_clockErr) {
        // socket broadcast догонит
      }
    } catch (error) {
      showMsg(getApiErrorMessage(error, t('gmSaveError')), 'error');
    } finally {
      setBusy('');
    }
  };

  const submitQuickEvent = async ({
    eventId, playerId, teamId, assistId, minute, second, wasSecondYellow,
  }) => {
    const match = state?.match || selectedMatch;
    if (!match?.id || !canWrite) return;
    const clock = toEventTime(
      minute,
      second,
      match.half_time,
      match?.settings?.extra_half_time || match?.extra_half_time,
    );
    setBusy('event');
    try {
      await authorizedPost(dispatch, API_URLS.addAdminMatchEvent, {
        match_id: Number(match.id),
        event_id: Number(eventId),
        player_id: Number(playerId),
        team_id: Number(teamId),
        additional_player_id: assistId ? Number(assistId) : null,
        time: clock.time,
        // Событие «сейчас» — тайм по текущему этапу матча, а не по минуте.
        time_number: timeNumberForStatus(
          localClockLive ? onlineClock.clock.status : statusForPhase(clockPhase, true),
        ) || clock.time_number,
        comment: '',
        note: '',
      });
      const player = effectiveRoster.find((item) => Number(item.id) === Number(playerId));
      const name = player ? playerDisplayName(player) : '';
      if (wasSecondYellow && name) showMsg(t('olm2YC', { name }));
      else showMsg(t('gmEventSaved'));
      setPlayerMenu(null);
      // Socket обновит state; добираем заявку на случай пустого applications
      try {
        const response = await getTabloState(dispatch, match.id);
        const nextState = extractTabloState(response.data);
        if (nextState) {
          setState((prev) => ({
            ...(prev || {}),
            ...nextState,
            clock: preferTabloClock(nextState.clock, prev?.clock),
            layers: mergeTabloLayers(prev?.layers, nextState.layers),
            layer_urls: {
              ...(prev?.layer_urls || {}),
              ...(nextState.layer_urls || {}),
            },
            closed: false,
          }));
        }
      } catch (_err) { /* socket догонит */ }
    } catch (error) {
      showMsg(getApiErrorMessage(error, t('gmSaveError')), 'error');
    } finally {
      setBusy('');
    }
  };

  const openPlayerMenu = (player) => {
    if (!canWrite || !player) return;
    setPlayerMenu(player);
  };

  const renderRosterColumn = (side, players) => {
    const match = activeMatchForRoster;
    return (
      <div className={`tablo-roster-col is-${side}`}>
        <header className="tablo-roster-head">
          <img
            src={matchTeamImage(match, side)}
            alt=""
            onError={(event) => { event.currentTarget.src = defaultTeamLogo; }}
          />
          <div>
            <strong>{matchTeamFullName(match, side)}</strong>
            <em>
              {side === 'home'
                ? (state?.match?.home_team_scores ?? match?.home_team_scores ?? 0)
                : (state?.match?.guest_team_scores ?? match?.guest_team_scores ?? 0)}
            </em>
          </div>
        </header>
        <div className="tablo-roster-list">
          {players.length ? players.map((player) => {
            const num = playerShirtNumber(player);
            return (
              <button
                key={player.id}
                type="button"
                className="tablo-roster-row"
                disabled={!canWrite || busy === 'event'}
                onClick={() => openPlayerMenu(player)}
              >
                <span className="tablo-roster-num">{num || '—'}</span>
                <span className="tablo-roster-name">{playerDisplayName(player)}</span>
              </button>
            );
          }) : (
            <p className="tablo-roster-empty">{t('tabloNoLineup')}</p>
          )}
        </div>
      </div>
    );
  };

  const sceneLabel = (item) => (lang === 'en' ? (item.labelEn || item.label) : item.label);

  const activeMatch = (state && state.match)
    ? { ...(selectedMatch || {}), ...(state.match || {}) }
    : (selectedMatch || null);

  const handleChangeMatch = () => {
    setMatchId(null);
    setMessage({ text: '', type: '' });
  };

  const matchPickSection = (
    <section className="st-block tablo-pick">
      <h2 className="st-block-header">{t('tabloPickMatchTitle')}</h2>
      <div className="tablo-filters">
        <input
          type="date"
          className="st-input"
          value={dateIso}
          onChange={(event) => setDateIso(event.target.value || toIsoDate(new Date()))}
        />
        <div className="st-search">
          <FiSearch className="st-search-icon" />
          <input
            type="text"
            className="st-search-input"
            placeholder={t('tabloMatchSearchPh')}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </div>
        <button type="button" className="st-btn st-btn-secondary" onClick={loadMatches} disabled={matchesLoading}>
          <FiRefreshCw /> {t('tabloRefresh')}
        </button>
      </div>

      {matchesLoading ? (
        <div className="tablo-loading">
          <Loader type="Oval" color="#3ad1a0" height={36} width={36} />
        </div>
      ) : (
        <div className="tablo-match-list">
          {matches.length ? matches.map((match) => (
            <button
              type="button"
              key={match.id}
              className="tablo-match-item"
              onClick={() => setMatchId(Number(match.id))}
            >
              <img
                src={matchTeamImage(match, 'home')}
                alt=""
                onError={(event) => { event.currentTarget.src = defaultTeamLogo; }}
              />
              <span className="tablo-match-names">
                <strong>
                  {matchTeamName(match, 'home')} — {matchTeamName(match, 'guest')}
                </strong>
                <em>
                  {[match.time, t(matchStatusKey(match)), scoreText(match)].filter(Boolean).join(' · ')}
                </em>
              </span>
              <img
                src={matchTeamImage(match, 'guest')}
                alt=""
                onError={(event) => { event.currentTarget.src = defaultTeamLogo; }}
              />
            </button>
          )) : (
            <p className="st-hint">{t('tabloNoMatches')}</p>
          )}
        </div>
      )}
    </section>
  );

  return (
    <div className={`st-layout tablo-panel ${matchId ? 'has-match' : 'is-pick-mode'}`}>
      {matchId ? (
        <div className="tablo-preview-float">
          <div className="tablo-preview tablo-preview-wide">
            <TabloBoard
              state={state ? {
                ...state,
                scene: resultPulse ? 'result' : scene,
                picture_type: resultPulse ? 'result' : scene,
                scoreboard_visible: scoreVisible,
              } : state}
              compact
              scoreVisible={scoreVisible}
              eventVisible={eventVisible}
            />
          </div>
        </div>
      ) : null}

      <div className="st-scroll">
        <StadiumCrumbs items={[{ label: t('navTablo') }]} />

        {message.text ? (
          <div className={`st-toast is-${message.type || 'success'}`}>{message.text}</div>
        ) : null}

        {!matchId ? (
          <div className="tablo-grid is-pick">
            {matchPickSection}
          </div>
        ) : (
          <>
            <div className="tablo-match-bar">
              <button type="button" className="st-btn st-btn-secondary tablo-change-match" onClick={handleChangeMatch}>
                <FiArrowLeft /> {t('tabloChangeMatch')}
              </button>
              <div className="tablo-match-bar-info">
                <div className="tablo-match-teams">
                  <span className="tablo-match-side">
                    <img
                      src={matchTeamImage(activeMatch, 'home')}
                      alt=""
                      onError={(event) => { event.currentTarget.src = defaultTeamLogo; }}
                    />
                    <strong>{matchTeamFullName(activeMatch, 'home')}</strong>
                  </span>
                  <span className="tablo-match-vs">—</span>
                  <span className="tablo-match-side">
                    <img
                      src={matchTeamImage(activeMatch, 'guest')}
                      alt=""
                      onError={(event) => { event.currentTarget.src = defaultTeamLogo; }}
                    />
                    <strong>{matchTeamFullName(activeMatch, 'guest')}</strong>
                  </span>
                </div>
                <em className="tablo-match-meta">
                  {[
                    activeMatch?.time,
                    activeMatch ? t(matchStatusKey(activeMatch)) : null,
                    scoreText(activeMatch || state?.match),
                  ].filter(Boolean).join(' · ')}
                </em>
              </div>
              <div className="tablo-match-bar-end">
                {live ? <span className="tablo-live-badge">{t('tabloLive')}</span> : null}
              </div>
            </div>

            <div className="tablo-grid is-work">
              <section className="st-block tablo-session">
                <h2 className="st-block-header">{t('tabloSessionTitle')}</h2>
                <div className="tablo-session-body">
                  <div className="st-field">
                    <label>{t('tabloScoreGroup')}</label>
                    <div className="tablo-scene-list">
                      {scenes.filter((item) => isTabloScoreScene(item.key)).map((item) => (
                        <button
                          type="button"
                          key={item.key}
                          className={`tablo-scene-chip is-toggle ${scoreVisible ? 'is-active' : ''}`}
                          disabled={!!busy}
                          onClick={() => handleSceneClick(item.key)}
                        >
                          {sceneLabel(item)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <label className={`st-check tablo-event-pulses ${!canWrite ? 'is-disabled' : ''}`}>
                    <input
                      type="checkbox"
                      checked={eventPulses}
                      disabled={!!busy || !canWrite}
                      onChange={handleEventPulsesToggle}
                    />
                    <span>{t('tabloEventPulses')}</span>
                  </label>
                  <p className="st-hint">{t('tabloEventPulsesHint')}</p>

                  {TABLO_SCENE_GROUPS.map((group) => (
                    <div className="st-field tablo-scene-group" key={group.id}>
                      <label>{t(group.labelKey)}</label>
                      <div className="tablo-scene-list">
                        {group.keys.map((key) => {
                          const item = scenes.find((sceneItem) => sceneItem.key === key)
                            || TABLO_FALLBACK_SCENES.find((sceneItem) => sceneItem.key === key);
                          if (!item) return null;
                          const isActive = scene === item.key && group.id === activeSceneGroupId;
                          return (
                            <button
                              type="button"
                              key={`${group.id}-${item.key}`}
                              className={`tablo-scene-chip ${isActive ? 'is-active' : ''}`}
                              disabled={!!busy}
                              onClick={() => handleSceneClick(item.key)}
                            >
                              {sceneLabel(item)}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                  <p className="st-hint">{t('tabloScoreToggleHint')}</p>

                  <div className="st-field">
                    <label>{t('tabloUnderlozhka')}</label>
                    <div className="tablo-under-list">
                      {underlozhki.map((item) => (
                        <button
                          type="button"
                          key={item.key}
                          className={`tablo-under-dot ${theme === item.key ? 'is-active' : ''}`}
                          style={{
                            background: item.accent,
                            boxShadow: `inset 0 0 0 1px ${item.backgroundColor || item.accent}`,
                          }}
                          title={item.label}
                          disabled={!!busy}
                          onClick={() => handleUnderlozhkaClick(item)}
                        />
                      ))}
                    </div>
                  </div>

                  <div className="st-field">
                    <label>{t('tabloAccent')}</label>
                    <div className="tablo-color-row">
                      <input
                        type="color"
                        value={accent || selectedUnder?.accent || '#1550F5'}
                        onChange={(event) => setAccent(event.target.value)}
                        onBlur={(event) => handleAccentCommit(event.target.value)}
                        disabled={!!busy}
                      />
                      <input
                        type="text"
                        className="st-input"
                        placeholder={selectedUnder?.accent || '#1550F5'}
                        value={accent}
                        onChange={(event) => setAccent(event.target.value)}
                        onBlur={(event) => handleAccentCommit(event.target.value)}
                        disabled={!!busy}
                      />
                    </div>
                  </div>
                  {styleSource ? <p className="st-hint">{t('tabloStyleSource')}: {styleSource}</p> : null}

                  <IfCanWrite entity="matches">
                    <div className="tablo-session-actions">
                      <button
                        type="button"
                        className="st-btn st-btn-primary"
                        disabled={busy === 'session'}
                        onClick={() => handleCreateSession({ withStyle: true })}
                      >
                        {busy === 'session' ? t('authLoading') : (session?.overlay_key ? t('tabloUpdateSession') : t('tabloCreateSession'))}
                      </button>
                    </div>
                  </IfCanWrite>

                  {session?.overlay_key ? (
                    <div className="tablo-overlay-box">
                      <p className="st-hint">
                        {t('tabloOverlayKey')}: <code>{session.overlay_key}</code>
                      </p>
                      <input className="st-input" readOnly value={overlayPageUrl} onFocus={(e) => e.target.select()} />
                      <div className="tablo-overlay-actions">
                        <button type="button" className="st-btn st-btn-secondary" onClick={copyOverlayUrl}>
                          <FiCopy /> {t('tabloCopyUrl')}
                        </button>
                        <button type="button" className="st-btn st-btn-secondary" onClick={openOverlay}>
                          <FiExternalLink /> {t('tabloOpenOverlay')}
                        </button>
                        <Link
                          to={`/manage_match/${matchId}`}
                          className="st-btn st-btn-secondary"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {t('tabloOpenMatch')}
                        </Link>
                        <IfCanWrite entity="matches">
                          <button type="button" className="st-btn st-btn-secondary" disabled={!!busy} onClick={handleRotateKey}>
                            <FiKey /> {busy === 'rotate' ? t('authLoading') : t('tabloRotateKey')}
                          </button>
                          <button type="button" className="st-btn st-btn-secondary tablo-btn-danger" disabled={!!busy} onClick={handleCloseSession}>
                            <FiXCircle /> {busy === 'close' ? t('authLoading') : t('tabloCloseAir')}
                          </button>
                        </IfCanWrite>
                      </div>
                    </div>
                  ) : (
                    <p className="st-hint">{t('tabloNeedSession')}</p>
                  )}
                </div>
              </section>

              <div className="tablo-console">
                <section className="st-block tablo-console-timer">
                  <div className="tablo-timer-row">
                    <div className="tablo-timer-clock" aria-live="polite">
                      <span className="tablo-timer-digits">
                        {shownMinute}
                        <small>:{String(shownSecond).padStart(2, '0')}</small>
                      </span>
                      <em>
                        {t(matchStatusKey(activeMatch))}
                        {localClockLive && !isClockRunning(onlineClock.clock) && onlineClock.clock.status.startsWith('paused')
                          ? ` · ${t('tabloClockPaused')}`
                          : ''}
                      </em>
                    </div>
                    <div className="tablo-score-inline">
                      <div className="tablo-score-inline-side">
                        <button type="button" className="st-btn st-btn-secondary" disabled={!canWrite || !!busy} onClick={() => bumpScore('home', -1)}><FiMinus /></button>
                        <strong>{state?.match?.home_team_scores ?? activeMatch?.home_team_scores ?? 0}</strong>
                        <button type="button" className="st-btn st-btn-secondary" disabled={!canWrite || !!busy} onClick={() => bumpScore('home', 1)}><FiPlus /></button>
                      </div>
                      <span className="tablo-score-inline-sep">:</span>
                      <div className="tablo-score-inline-side">
                        <button type="button" className="st-btn st-btn-secondary" disabled={!canWrite || !!busy} onClick={() => bumpScore('guest', -1)}><FiMinus /></button>
                        <strong>{state?.match?.guest_team_scores ?? activeMatch?.guest_team_scores ?? 0}</strong>
                        <button type="button" className="st-btn st-btn-secondary" disabled={!canWrite || !!busy} onClick={() => bumpScore('guest', 1)}><FiPlus /></button>
                      </div>
                    </div>
                  </div>
                  {canWrite && (localClockLive || baseClock) ? (
                    <div className="tablo-clock-controls">
                      <OnlineClockBar matchId={matchId} variant="controls" baseClock={baseClock} hotkey={!playerMenu} />
                      <span className="st-hint">{t('olmSpaceHint')}</span>
                    </div>
                  ) : null}
                  <div className="tablo-play-controls is-console">
                    {playOptions.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        className={`st-btn st-btn-secondary ${phase === option.value ? 'is-active' : ''}`}
                        disabled={!canWrite || !!busy}
                        onClick={() => setPlay(option.value)}
                      >
                        {t(option.labelKey)}
                      </button>
                    ))}
                  </div>
                </section>

                <section className="st-block tablo-console-rosters">
                  <div className="tablo-roster-grid">
                    {renderRosterColumn('home', homePlayers)}
                    {renderRosterColumn('guest', guestPlayers)}
                  </div>
                  <p className="st-hint tablo-roster-hint">{t('tabloQuickHint')}</p>
                </section>
              </div>
            </div>

            {playerMenu ? (
              <TabloQuickPlayerMenu
                player={playerMenu}
                teammates={
                  Number(playerMenu.team_id) === Number(activeMatchForRoster?.home_team_id)
                    ? homePlayers
                    : guestPlayers
                }
                events={matchEvents}
                minute={shownMinute}
                second={shownSecond}
                busy={busy === 'event'}
                t={t}
                onClose={() => setPlayerMenu(null)}
                onSubmit={submitQuickEvent}
              />
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}
